import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { SubscribeInfoData } from '@/services/agent-v2-users-detail';
import { AiCreationImage, ArticleImage, ImageImage, MomentsImage, PodcastImage, VideoImage } from './assets';
import { AiSpeechImage } from './assets/icon/ai-speech-image';

interface SubscriptionSettingProps {
  subscribeInfo: SubscribeInfoData;
  loading: boolean;
  subscriptionVisibility: number[];
  changeValue: (key: keyof SubscribeInfoData, value: number) => void;
}

/** 订阅设置 */
export const SubscriptionSetting = ({
  subscribeInfo,
  loading,
  subscriptionVisibility,
  changeValue,
}: SubscriptionSettingProps) => {
  const { t } = useTranslation();

  const types = useMemo(() => {
    return [
      {
        name: t("agent_dashboard.user_detail.subscription_settings.video_settings"),
        desc: t("agent_dashboard.user_detail.subscription_settings.video_desc"),
        icon: VideoImage,
        value: subscribeInfo.videoSubscribe,
        visible: subscriptionVisibility.includes(1),
        onChange: (value: number) => changeValue('videoSubscribe', value),
      },
      {
        name: t("agent_dashboard.user_detail.subscription_settings.podcast_settings"),
        desc: t("agent_dashboard.user_detail.subscription_settings.podcast_desc"),
        icon: PodcastImage,
        value: subscribeInfo.blogSubscribe,
        visible: subscriptionVisibility.includes(2),
        onChange: (value: number) => changeValue('blogSubscribe', value),
      },
      {
        name: t("agent_dashboard.user_detail.subscription_settings.moments_settings"),
        desc: t("agent_dashboard.user_detail.subscription_settings.moments_desc"),
        icon: MomentsImage,
        value: subscribeInfo.momentsSubscribe,
        visible: subscriptionVisibility.includes(3),
        onChange: (value: number) => changeValue('momentsSubscribe', value),
      },
      {
        name: t("agent_dashboard.user_detail.subscription_settings.image_settings"),
        desc: t("agent_dashboard.user_detail.subscription_settings.image_desc"),
        icon: ImageImage,
        value: subscribeInfo.graphicSubscribe,
        visible: subscriptionVisibility.includes(4),
        onChange: (value: number) => changeValue('graphicSubscribe', value),
      },
      {
        name: t("agent_dashboard.user_detail.subscription_settings.article_settings"),
        desc: t("agent_dashboard.user_detail.subscription_settings.article_desc"),
        icon: ArticleImage,
        value: subscribeInfo.articleSubscribe,
        visible: subscriptionVisibility.includes(5),
        onChange: (value: number) => changeValue('articleSubscribe', value),
      },
      {
        name: t("agent_dashboard.user_detail.subscription_settings.ai_creation_settings"),
        desc: t("agent_dashboard.user_detail.subscription_settings.ai_creation_desc"),
        icon: AiCreationImage,
        value: subscribeInfo.aiSubscribe,
        visible: subscriptionVisibility.includes(6),
        onChange: (value: number) => changeValue('aiSubscribe', value),
      },
      {
        name: t("agent_dashboard.user_detail.subscription_settings.ai_speech_settings"),
        desc: t("agent_dashboard.user_detail.subscription_settings.ai_speech_desc"),
        icon: AiSpeechImage,
        value: subscribeInfo.chatSubscribe,
        visible: subscriptionVisibility.includes(7),
        onChange: (value: number) => changeValue('chatSubscribe', value),
      }
    ]
  }, [subscribeInfo, subscriptionVisibility, changeValue])

  if (loading) {
    return renderSkeleton();
  }

  return (
    <div className="relative pb-6 border-b border-[rgba(233,234,235,1)]">
      <div className='text-lg font-semibold text-[rgba(16,24,40,1)] mb-6'>
        <span>{t("agent_dashboard.user_detail.subscription_settings.title")}</span>
      </div>
      <div className='grid grid-cols-4 gap-3'>
        {types.map(item => {
          return item.visible && (
            <div key={item.name} className='p-5 border border-[rgba(233,234,235,1)] rounded-lg'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <item.icon className='size-5' />
                  <span className='ml-2 text-[17px] font-semibold text-[rgba(16,24,40,1)]'>{item.name}</span>
                </div>
                <div>
                  <Switch
                    className='data-[state=checked]:bg-[rgba(18,183,106,1)]'
                    checked={!!item.value}
                    onCheckedChange={(checked) => item.onChange(Number(checked))}
                  />
                </div>
              </div>
              <div className='text-xs mt-1 text-[rgba(71,84,103,1)] pl-7'>{item.desc}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// 骨架屏组件
const renderSkeleton = () => {
  const skeletonItems = Array(6).fill(0);
  return (
    <div className="relative pb-6 border-b border-[rgba(233,234,235,1)]">
      <div className='text-lg font-semibold text-[rgba(16,24,40,1)] mb-6'>
        <div className="animate-pulse bg-gray-200 h-6 rounded w-40"></div>
      </div>
      <div className='grid grid-cols-3 gap-5'>
        {skeletonItems.map((_, index) => (
          <div key={index} className='p-5 border border-[rgba(233,234,235,1)] rounded-lg animate-pulse'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center'>
                <div className="bg-gray-200 p-1.5 rounded-md">
                  <div className='size-5 text-gray-300' />
                </div>
                <div className='ml-2 animate-pulse bg-gray-200 h-5 rounded w-32'></div>
              </div>
              <div className='animate-pulse bg-gray-200 h-6 w-12 rounded-full'></div>
            </div>
            <div className='mt-1 animate-pulse bg-gray-200 h-3 rounded w-40 pl-7'></div>
          </div>
        ))}
      </div>
    </div>
  );
};